// === ./src/frontend/components/TorrentMockup.jsx ===
import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Stack,
  Chip,
  LinearProgress,
  alpha,
  useTheme,
} from "@mui/material";
import {
  CloudDownload,
  Search,
  Speed,
  CheckCircle,
  Wifi,
} from "@mui/icons-material";
import { float, blink, pulse } from "./animations";
import { TranslateContext } from "../../context/TranslateContext";

const TorrentMockup = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { lang } = useContext(TranslateContext);
  const [progress, setProgress] = useState(0);
  const [speed, setSpeed] = useState(0);
  const [peers, setPeers] = useState(0);
  const [phase, setPhase] = useState("searching");

  useEffect(() => {
    let t;
    if (phase === "searching") {
      t = setTimeout(() => setPhase("connecting"), 1800);
    } else if (phase === "connecting") {
      setPeers(0);
      let p = 0;
      const iv = setInterval(() => {
        p += Math.floor(Math.random() * 8) + 3;
        setPeers(Math.min(p, 47));
        if (p >= 47) {
          clearInterval(iv);
          setPhase("downloading");
        }
      }, 120);
      return () => clearInterval(iv);
    } else if (phase === "downloading") {
      let prog = 0;
      const iv = setInterval(() => {
        prog += Math.random() * 2.5 + 0.5;
        setProgress(Math.min(prog, 100));
        setSpeed(+(Math.random() * 8 + 4).toFixed(1));
        if (prog >= 100) {
          clearInterval(iv);
          setPhase("done");
        }
      }, 120);
      return () => clearInterval(iv);
    } else if (phase === "done") {
      t = setTimeout(() => {
        setProgress(0);
        setSpeed(0);
        setPeers(0);
        setPhase("searching");
      }, 3500);
    }
    return () => clearTimeout(t);
  }, [phase]);

  const bg = isDark ? alpha(theme.palette.background.paper, 0.95) : "#ffffff";

  const phaseLabels = {
    searching: lang?.mockupPhaseSearching || "searching",
    connecting: lang?.mockupPhaseConnecting || "connecting",
    downloading: lang?.mockupPhaseDownloading || "downloading",
    done: lang?.mockupPhaseDone || "done",
  };

  const phases = ["searching", "connecting", "downloading", "done"];

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 420,
        borderRadius: 3,
        overflow: "hidden",
        boxShadow: isDark
          ? `0 24px 80px ${alpha("#000", 0.6)}`
          : `0 24px 60px ${alpha(theme.palette.primary.main, 0.18)}`,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
        bgcolor: bg,
        animation: `${float} 4s ease-in-out infinite`,
      }}
    >
      {/* Title Bar */}
      <Box
        sx={{
          px: 2,
          py: 1.25,
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary?.main || alpha(theme.palette.primary.main, 0.7)})`,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <CloudDownload sx={{ color: "#fff", fontSize: 18 }} />
        <Typography variant="caption" fontWeight={700} color="#fff">
          {lang?.mockupTitle || "TorrentCloud — Download Manager"}
        </Typography>
        <Box sx={{ ml: "auto", display: "flex", gap: 0.5 }}>
          {["#ff5f57", "#febc2e", "#28c840"].map((c) => (
            <Box
              key={c}
              sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: c }}
            />
          ))}
        </Box>
      </Box>

      <Box sx={{ p: 2.5 }}>
        {/* Search bar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            p: 1.25,
            borderRadius: 2,
            mb: 2,
            border: `1.5px solid ${alpha(theme.palette.primary.main, 0.25)}`,
            bgcolor: alpha(theme.palette.primary.main, 0.04),
          }}
        >
          <Search sx={{ fontSize: 16, color: "text.secondary" }} />
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            {phase === "searching" ? (
              <Box component="span" sx={{ animation: `${blink} 1s infinite` }}>
                ubuntu-24.04.2-desktop-amd64.iso▌
              </Box>
            ) : (
              "ubuntu-24.04.2-desktop-amd64.iso"
            )}
          </Typography>
          <Chip
            label={lang?.mockupSearchBtn || "Search"}
            size="small"
            color="primary"
            sx={{ height: 20, fontSize: "0.6rem", fontWeight: 700 }}
          />
        </Box>

        {/* Phase stepper */}
        <Stack direction="row" spacing={1} mb={2} alignItems="center">
          {phases.map((p, i) => (
            <React.Fragment key={p}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor:
                      phases.indexOf(phase) >= i
                        ? theme.palette.primary.main
                        : alpha(theme.palette.divider, 0.5),
                    transition: "background 0.4s",
                    ...(phase === p && {
                      animation: `${pulse} 1s infinite`,
                    }),
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.6rem",
                    color: "text.secondary",
                    textTransform: "capitalize",
                  }}
                >
                  {phaseLabels[p]}
                </Typography>
              </Box>
              {i < 3 && <Box sx={{ flex: 1, height: 1, bgcolor: "divider" }} />}
            </React.Fragment>
          ))}
        </Stack>

        {/* Peer visualization */}
        {(phase === "connecting" ||
          phase === "downloading" ||
          phase === "done") && (
          <Box
            sx={{
              position: "relative",
              height: 110,
              mb: 2,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                zIndex: 2,
                position: "relative",
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.7)})`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 0 20px ${alpha(theme.palette.primary.main, 0.4)}`,
              }}
            >
              <CloudDownload sx={{ color: "#fff", fontSize: 22 }} />
            </Box>
            {[0, 60, 120, 180, 240, 300].map((deg, i) => (
              <Box
                key={i}
                sx={{
                  position: "absolute",
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  bgcolor:
                    i < Math.floor(peers / 8)
                      ? alpha(theme.palette.success.main, 0.9)
                      : alpha(theme.palette.divider, 0.4),
                  border: `2px solid ${i < Math.floor(peers / 8) ? theme.palette.success.main : "transparent"}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.5s ease",
                  transform: `rotate(${deg}deg) translateX(42px) rotate(-${deg}deg)`,
                  boxShadow:
                    i < Math.floor(peers / 8)
                      ? `0 0 8px ${alpha(theme.palette.success.main, 0.5)}`
                      : "none",
                }}
              >
                <Wifi
                  sx={{
                    fontSize: 10,
                    color: i < Math.floor(peers / 8) ? "#fff" : "text.disabled",
                  }}
                />
              </Box>
            ))}
            <Box sx={{ position: "absolute", bottom: 6, right: 10 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.6rem" }}
              >
                {peers} {lang?.mockupPeersConnected || "peers connected"}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Progress */}
        {(phase === "downloading" || phase === "done") && (
          <Box>
            <Stack direction="row" justifyContent="space-between" mb={0.5}>
              <Typography
                variant="caption"
                fontWeight={600}
                sx={{ fontSize: "0.72rem" }}
              >
                ubuntu-24.04.2-desktop-amd64.iso
              </Typography>
              <Typography
                variant="caption"
                fontWeight={700}
                color="primary.main"
                sx={{ fontSize: "0.72rem" }}
              >
                {progress.toFixed(1)}%
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 7,
                borderRadius: 4,
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                "& .MuiLinearProgress-bar": {
                  borderRadius: 4,
                  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary?.main || alpha(theme.palette.primary.main, 0.7)})`,
                },
              }}
            />
            <Stack direction="row" justifyContent="space-between" mt={0.75}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Speed sx={{ fontSize: 12, color: "success.main" }} />
                <Typography
                  variant="caption"
                  color="success.main"
                  fontWeight={600}
                  sx={{ fontSize: "0.65rem" }}
                >
                  {phase === "done"
                    ? lang?.mockupComplete || "Complete!"
                    : `${speed} MB/s`}
                </Typography>
              </Stack>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.65rem" }}
              >
                {lang?.mockupFileInfo || "5.2 GB • ISO Image"}
              </Typography>
            </Stack>
          </Box>
        )}

        {/* Done state */}
        {phase === "done" && (
          <Box
            sx={{
              mt: 1.5,
              p: 1.25,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.success.main, 0.08),
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <CheckCircle sx={{ fontSize: 16, color: "success.main" }} />
            <Typography
              variant="caption"
              color="success.main"
              fontWeight={600}
              sx={{ fontSize: "0.7rem" }}
            >
              {lang?.mockupSavedMsg ||
                "Saved to your cloud storage — accessible anywhere!"}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TorrentMockup;
